import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import UserPlusIcon from "./icons/UserPlusIcon";
import TicketIcon from "./icons/TicketIcon";
import UsersGroupIcon from "./icons/UsersGroupIcon";
import ShieldCheckIcon from "./icons/ShieldCheckIcon";
import ClipboardListIcon from "./icons/ClipboardListIcon";
import ShieldExclamationIcon from "./icons/ShieldExclamationIcon";
import type { NotificationItem } from "../hooks/useAdminNotifications";

interface NotificationRowProps {
    item: NotificationItem;
    /** Close the Notification Center panel after navigating. */
    onClose?: () => void;
    /** Amber accent + dot. Pending items are always unread; seen informational ones are not. */
    unread?: boolean;
}

// Per-type icon + tint. Logical (start/end) classes only for RTL safety.
const TYPE_META: Record<
    NotificationItem["type"],
    { Icon: React.FC<React.SVGProps<SVGSVGElement>>; tint: string }
> = {
    referee_proposal: {
        Icon: UserPlusIcon,
        tint: "text-sky-300 bg-sky-500/15 border-sky-500/30",
    },
    account: {
        Icon: UserPlusIcon,
        tint: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
    },
    demand: {
        Icon: TicketIcon,
        tint: "text-violet-300 bg-violet-500/15 border-violet-500/30",
    },
    request: {
        Icon: ClipboardListIcon,
        tint: "text-sky-300 bg-sky-500/15 border-sky-500/30",
    },
    league_access: {
        Icon: ShieldCheckIcon,
        tint: "text-amber-300 bg-amber-500/15 border-amber-500/30",
    },
    organization: {
        Icon: UsersGroupIcon,
        tint: "text-orange-300 bg-orange-500/15 border-orange-500/30",
    },
    pv_report_check: {
        Icon: ShieldExclamationIcon,
        tint: "text-amber-300 bg-amber-500/15 border-amber-500/30",
    },
    motif_accumulation: {
        Icon: ShieldExclamationIcon,
        tint: "text-orange-300 bg-orange-500/15 border-orange-500/30",
    },
    official_request: {
        Icon: ShieldCheckIcon,
        tint: "text-sky-300 bg-sky-500/15 border-sky-500/30",
    },
    official_link: {
        Icon: UsersGroupIcon,
        tint: "text-sky-300 bg-sky-500/15 border-sky-500/30",
    },
};

/**
 * A single pending-notification row. Every item surfaced here is by definition
 * "pending" (the hook only projects untreated records), so each row carries the
 * amber left-accent + unread dot. Once treated, the record leaves the query
 * projection and the row disappears — that is the "restyle".
 */
export const NotificationRow: React.FC<NotificationRowProps> = React.memo(
    ({ item, onClose, unread = true }) => {
        const { t } = useTranslation("notifications");
        const navigate = useNavigate();
        const { Icon, tint } = TYPE_META[item.type];

        const relativeTime = useMemo(() => {
            if (item.timeless) return "";
            const diff = Date.now() - new Date(item.createdAt).getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return t("time_just_now");
            if (mins < 60) return t("time_minutes_ago", { count: mins });
            const hours = Math.floor(mins / 60);
            if (hours < 24) return t("time_hours_ago", { count: hours });
            const days = Math.floor(hours / 24);
            if (days < 7) return t("time_days_ago", { count: days });
            return t("time_weeks_ago", { count: Math.floor(days / 7) });
        }, [item.createdAt, item.timeless, t]);

        const meta = [relativeTime, item.subtitle].filter(Boolean).join(" · ");

        const handleClick = () => {
            onClose?.();
            navigate(item.route);
        };

        return (
            <button
                type="button"
                onClick={handleClick}
                data-unread={unread ? "true" : "false"}
                className={`w-full text-start flex items-center gap-3 px-3 py-2.5 border-s-2 ${unread ? "border-amber-400 bg-amber-500/5" : "border-s-transparent"} hover:bg-white/5 border-b border-slate-700/60 last:border-b-0 transition-colors`}
            >
                <span
                    className={`relative shrink-0 inline-flex items-center justify-center rounded-lg border p-2 ${tint}`}
                >
                    <Icon className="h-4 w-4" />
                    {/* Unread dot */}
                    {unread && (
                        <span className="absolute -top-1 -end-1 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-[#1e293b]" />
                    )}
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-white">
                        {item.title}
                    </span>
                    <span className="block truncate text-xs text-white/50">
                        {meta}
                    </span>
                </span>
                <ChevronRight
                    size={16}
                    className="shrink-0 text-white/30 rtl:rotate-180"
                />
            </button>
        );
    }
);

NotificationRow.displayName = "NotificationRow";

export default NotificationRow;
